import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import type { HttpClient } from '@wix/cli-http-client';
import { getTestOverrides } from '@wix/cli-test-overrides';
import {
  getBackendDeployment,
  createBackendDeployment,
  updateBackendDeployment,
} from '@wix/ambassador-ctp-app-cloud-backend-service-v1-app-cloud-backend-deployment/http';
import { BackendDeploymentStatus } from '@wix/ambassador-ctp-app-cloud-backend-service-v1-app-cloud-backend-deployment/types';
import type {
  CreateBackendDeploymentRequest,
  GetBackendDeploymentRequest,
  UpdateBackendDeploymentRequest,
} from '@wix/ambassador-ctp-app-cloud-backend-service-v1-app-cloud-backend-deployment/types';
import { CliError, CliErrorCode } from '@wix/cli-error';
import pWaitFor from 'p-wait-for';
import type { BiLogger } from '@wix/cli-telemetry';
import { backendDeploymentStateSchema } from './schemas.js';
import { wixCliAppBiEvents } from '../bi/events.js';

export class BackendDeploymentClient {
  constructor(
    private readonly biLogger: BiLogger,
    private readonly httpClient: HttpClient,
    private readonly retryOptions: Options = {
      retries: 3,
      minTimeout: getTestOverrides().minRetryTimeout,
      maxTimeout: 3 * 1000,
    }
  ) {}

  async createBackendDeployment(req: CreateBackendDeploymentRequest) {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(createBackendDeployment(req)),
        this.retryOptions
      );

      return backendDeploymentStateSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateBackendDeployment(),
        cause: e,
      });
    }
  }

  async updateBackendDeployment(req: UpdateBackendDeploymentRequest) {
    try {
      const biEvents = wixCliAppBiEvents(this.biLogger);
      const deploymentBi = biEvents.cliDeployment({
        appId: req.appId,
        deploymentId: req.deploymentId,
        files: req.files,
      });

      await pRetry(
        () => this.httpClient.request(updateBackendDeployment(req)),
        this.retryOptions
      );

      const backendDeployment = await this.waitForDeployment(
        req.appId,
        req.deploymentId
      );

      deploymentBi.finish({
        isSuccess:
          backendDeployment.status ===
          BackendDeploymentStatus.BACKEND_DEPLOYMENT_SUCCESS,
        failureReason: backendDeployment.error,
      });

      if (
        backendDeployment.status ===
        BackendDeploymentStatus.BACKEND_DEPLOYMENT_ERROR
      ) {
        throw new CliError({
          code: CliErrorCode.FailedToDeployBackend(),
          info: {
            ...backendDeployment,
          },
          cause: null,
        });
      }

      return backendDeploymentStateSchema.parse(backendDeployment);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUpdateBackendDeployment(),
        cause: e,
      });
    }
  }

  async getBackendDeployment(req: GetBackendDeploymentRequest) {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(getBackendDeployment(req)),
        this.retryOptions
      );

      return backendDeploymentStateSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetBackendDeployment(),
        cause: e,
      });
    }
  }

  private async waitForDeployment(appId: string, deploymentId: string) {
    const seconds = (n: number) => 1000 * n;
    const minutes = (n: number) => seconds(60) * n;

    try {
      return await pWaitFor(
        async () => {
          const backendDeployment = await this.getBackendDeployment({
            appId,
            deploymentId,
          });

          if (
            backendDeployment.status ===
            BackendDeploymentStatus.BACKEND_DEPLOYMENT_IN_PROGRESS
          ) {
            return false;
          }

          return pWaitFor.resolveWith(backendDeployment);
        },
        {
          before: false,
          interval: seconds(3),
          timeout: minutes(5),
        }
      );
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToDeployBackend(),
        cause: e,
        info: {
          appId,
          deploymentId,
        },
      });
    }
  }
}
